import type { AssistantRuntime, Tool } from '@assistant-ui/react';
import { AssistantChatTransport, frontendTools } from '@assistant-ui/react-ai-sdk';
import { encode } from '@toon-format/toon';
import {
  type ChatRequestOptions,
  type ChatTransport,
  convertToModelMessages,
  createIdGenerator,
  type HttpChatTransportInitOptions,
  type LanguageModel,
  type ModelMessage,
  pruneMessages,
  safeValidateUIMessages,
  streamText,
  type TextPart,
  type ToolCallPart,
  type ToolResultPart,
  type ToolSet,
  type UIMessage,
  type UIMessageChunk,
} from 'ai';
import dayjs from 'dayjs';

import { isModelToolCallPart, isTextPart, isToolResultPart } from './message-part-guards.ts';
import type { AtlasMessage } from './types.ts';
import { AskUserQuestionTool } from '#plugin-chat/components/tools/ask-user-question/AskUserQuestionTool.tsx';
import { QueryKnowledgeGraphTool } from '#plugin-chat/components/tools/query-knowledge-graph/QueryKnowledgeGraphTool.tsx';
import { TransformDatasetTool } from '#plugin-chat/components/tools/transform-dataset/TransformDatasetTool.tsx';

// Prevent excessive token usage from large tool outputs
const MAX_OUTPUT_LENGTH = 50000;

/** Debug metadata attached to transformed messages for troubleshooting the transformation pipeline. */
export type TransformDebugInfo = {
  originalType: 'tool-call' | 'tool-result' | 'passthrough';
  toolName?: string;
};

/** Message format after transforming tool calls/results into text-only messages compatible with models that don't support native tool use. */
export type TransformedMessage = (
  | {
      role: 'user' | 'assistant';
      content: TextPart[];
    }
  | {
      role: 'system';
      content: string;
    }
) & {
  _debug?: TransformDebugInfo;
};

/** Context available to transformation functions, providing access to all messages for cross-message analysis. */
export type TransformContext = {
  messages: ModelMessage[];
};

/** Transforms a tool call into a custom text message representation. */
export type ToolCallTransformer = (part: ToolCallPart) => TransformedMessage | null;

/** Transforms a tool result into a custom text message representation with access to message context. */
export type ToolResultTransformer = (part: ToolResultPart, ctx: TransformContext) => TransformedMessage | null;

/** Custom transformation handlers for specific tools, allowing domain-specific formatting of tool calls and results. */
export type ToolTransformation = {
  call?: ToolCallTransformer;
  result?: ToolResultTransformer;
};

function isModelMessages(messages: unknown): messages is ModelMessage[] {
  if (!Array.isArray(messages)) {
    return false;
  }
  return messages.every(
    (msg) =>
      typeof msg === 'object' &&
      msg !== null &&
      'role' in msg &&
      typeof msg.role === 'string' &&
      ['user', 'assistant', 'system', 'tool'].includes(msg.role) &&
      'content' in msg,
  );
}

function isTextOnlyMessage(msg: ModelMessage): msg is ModelMessage & { content: TextPart[] } {
  if (!Array.isArray(msg.content)) {
    return false;
  }
  return msg.content.every(isTextPart);
}

const hasToolCallContent = (content: unknown): boolean => {
  if (!Array.isArray(content)) {
    return false;
  }
  return content.some(isModelToolCallPart);
};

/** Creates a text-only message with optional debug metadata. */
export function createTextMessage(
  role: TransformedMessage['role'],
  text: string,
  debug?: TransformDebugInfo,
): TransformedMessage {
  // NOTE: System messages use string content, others use TextPart array
  if (role === 'system') {
    return {
      role: 'system',
      content: text,
      ...(debug && { _debug: debug }),
    };
  }
  return {
    role,
    content: [{ type: 'text' as const, text }],
    ...(debug && { _debug: debug }),
  };
}

/** Type-safe accessor for tool call input parameters. */
export const getToolInput = <InputType>(part: ToolCallPart): InputType => part.input as InputType;

/** Extracts JSON output from tool result, returning null if output type is not JSON. */
export const getJsonOutput = <OutputType>(part: ToolResultPart): OutputType | null =>
  part.output.type === 'json' ? (part.output.value as OutputType) : null;

/** Serializes and truncates large values to prevent excessive token consumption in LLM context. */
function truncateOutput(value: unknown, maxLength = MAX_OUTPUT_LENGTH): string {
  const encoded = encode(value);
  if (encoded.length <= maxLength) {
    return encoded;
  }

  const truncated = encoded.slice(0, maxLength);
  return `${truncated}\n\n[... truncated ${encoded.length - maxLength} characters]`;
}

/**
 * Creates a part transformer that converts tool calls/results into text messages.
 * Uses generic formatting for all tool calls and results.
 */
function createTransformPart() {
  return (part: unknown, _ctx: TransformContext): (TransformedMessage | ModelMessage)[] | null => {
    if (isModelToolCallPart(part)) {
      if (part.toolName === 'plan') {
        // FIXME: Hard coded to prevent duplicate input/output, tools should be able to override input message too
        return null; //createTextMessage('system', 'Preparing plan...');
      }
      // Default: wrap tool calls in XML format for LLM understanding
      return [
        createTextMessage(
          'system',
          `Executing \`${part.toolName}\` tool call with the following parameters:

\`\`\`
${encode(part.input)}
\`\`\``,
          { originalType: 'tool-call', toolName: part.toolName },
        ),
      ];
    }

    if (isToolResultPart(part)) {
      // Errors use distinct XML tags to help LLM distinguish failures from successes
      if (part.output.type === 'error-text') {
        console.error(part);
        return [
          createTextMessage('system', `The ${part.toolName} tool call failed:\n${part.output.value}`, {
            originalType: 'tool-result',
            toolName: part.toolName,
          }),
        ];
      }
      if (part.output.type === 'error-json') {
        return [
          createTextMessage(
            'system',
            `<tool_error name="${part.toolName}">\n${truncateOutput(part.output.value)}\n</tool_error>`,
            { originalType: 'tool-result', toolName: part.toolName },
          ),
        ];
      }

      if (part.output.type === 'execution-denied') {
        return [
          createTextMessage('system', `<tool_error name="${part.toolName}">\nExecution denied\n</tool_error>`, {
            originalType: 'tool-result',
            toolName: part.toolName,
          }),
        ];
      }

      // Skip 'content' type outputs which need special handling not implemented yet
      if (part.output.type !== 'json' && part.output.type !== 'text') {
        return null;
      }

      // Prefer human-readable message from ToolResult interface over serialized data
      // Add success/failure prefix when success field is present for clear LLM signal
      const output = part.output.value as { success?: boolean; message?: string; messages?: ModelMessage[] } | unknown;
      const isSuccess = hasSuccessField(output) && output.success;

      if (typeof output === 'object' && output !== null && 'messages' in output && isModelMessages(output.messages)) {
        return output.messages;
      }

      const messageContent =
        typeof output === 'object' && output !== null && 'message' in output && typeof output.message === 'string'
          ? output.message
          : truncateOutput(part.output.value);
      return [
        createTextMessage(
          'system',
          `The \`${part.toolName}\` tool call ${isSuccess ? 'completed successfully' : 'failed'}:\n\n${messageContent}`,
          {
            originalType: 'tool-result',
            toolName: part.toolName,
          },
        ),
      ];
    }

    return null;
  };
}

const hasSuccessField = (output: unknown): output is { success: boolean } =>
  typeof output === 'object' && output !== null && 'success' in output && typeof output.success === 'boolean';

/**
 * Returns false for messages whose content is empty or whitespace-only.
 * Used to drop tool-result or passthrough messages that carry no meaningful text,
 * keeping the LLM context minimal.
 */
export function isNonEmptyMessage(msg: TransformedMessage): boolean {
  if (!Array.isArray(msg.content)) {
    return msg.content.trim().length > 0;
  }
  return msg.content.some((part) => part.text.trim().length > 0);
}
/**
 * Creates a message transformer that converts tool calls/results into text messages.
 * Tool calls are wrapped in code blocks, results are formatted with success/failure status.
 */
function createMessageTransformer() {
  const transformPart = createTransformPart();

  return (modelMessages: ModelMessage[]): TransformedMessage[] => {
    const ctx: TransformContext = { messages: modelMessages };

    const transformed = modelMessages.flatMap((msg): TransformedMessage[] => {
      const isToolRelated = (msg.role === 'assistant' && hasToolCallContent(msg.content)) || msg.role === 'tool';

      if (!isToolRelated) {
        // Type-safe passthrough for text-only messages
        if (isTextOnlyMessage(msg)) {
          if (msg.role === 'user' || msg.role === 'assistant') {
            return [{ role: msg.role, content: msg.content, _debug: { originalType: 'passthrough' } }];
          }
          if (msg.role === 'system') {
            // System messages should have string content per TransformedMessage type
            const textContent = msg.content.map((p) => p.text).join('\n');
            return [{ role: 'system', content: textContent, _debug: { originalType: 'passthrough' } }];
          }
        }
        // Handle string content format (convert to TextPart array)
        if (typeof msg.content === 'string') {
          if (msg.role === 'user' || msg.role === 'assistant') {
            return [
              {
                role: msg.role,
                content: [{ type: 'text' as const, text: msg.content }],
                _debug: { originalType: 'passthrough' },
              },
            ];
          }
          if (msg.role === 'system') {
            return [{ role: 'system', content: msg.content, _debug: { originalType: 'passthrough' } }];
          }
        }
        return [];
      }

      if (!Array.isArray(msg.content)) {
        return [];
      }

      return msg.content
        .flatMap((part) => transformPart(part, ctx))
        .filter((m): m is TransformedMessage => m !== null && m.content.length > 0);
    });

    return transformed.filter(isNonEmptyMessage);
  };
}

type FrontendToolsInput = Record<string, { description?: string; parameters: object }>;

/** Filters tools to only include enabled frontend tools, excluding disabled and backend-only tools. */
const getEnabledTools = (tools: Record<string, Tool>): FrontendToolsInput => {
  return Object.fromEntries(
    Object.entries(tools)
      .filter(([, tool]) => !tool.disabled && tool.type !== 'backend')
      .map(([name, tool]) => [name, { description: tool.description, parameters: tool.parameters ?? {} }]),
  );
};

/**
 * Custom chat transport that handles Atlas-specific message transformation and streaming.
 * Transforms tool calls/results into text messages and manages LLM streaming with the Vercel AI SDK.
 */
export class AtlasChatTransport extends AssistantChatTransport<AtlasMessage> implements ChatTransport<AtlasMessage> {
  private model: LanguageModel;
  private runtimeRef: AssistantRuntime | undefined;

  override setRuntime(runtime: AssistantRuntime) {
    super.setRuntime(runtime);
    // Cache runtime reference to access model context during message processing
    this.runtimeRef = runtime;
  }

  constructor(model: LanguageModel, initOptions?: HttpChatTransportInitOptions<UIMessage>) {
    super(initOptions);
    console.info('Creating AtlasChatTransport with model', model);
    this.model = model;
  }

  /**
   * Validates, converts, prunes, and transforms messages for LLM consumption.
   * Applies custom tool transformations and removes empty messages to minimize token usage.
   */
  async processMessages(
    // NOTE: Using broader type to work around @assistant-ui/react-ai-sdk's frontendTools returning
    // Schema<unknown> instead of FlexibleSchema - the runtime types are compatible
    tools: Record<string, { inputSchema?: unknown; description?: string }>,
    messages: AtlasMessage[],
  ) {
    console.debug('Processing messages for agent transport', { tools, messages });
    const validationResult = await safeValidateUIMessages<AtlasMessage>({
      messages,
    });
    if (validationResult.success === false) {
      console.error('Message validation failed:', validationResult.error);
      throw new Error('Message validation failed', { cause: validationResult.error });
    }
    // Cast tools to ToolSet - frontendTools returns compatible runtime types but with Schema<unknown>
    // instead of FlexibleSchema. This is a known type mismatch with AI SDK v6.
    const convertedMessages = await convertToModelMessages<AtlasMessage>(validationResult.data, {
      tools: tools as ToolSet,
      // Allow partial tool calls to support streaming scenarios
      ignoreIncompleteToolCalls: true,
    });

    const prunedMessages = pruneMessages({
      messages: convertedMessages,
      // Remove empty messages to reduce token usage
      emptyMessages: 'remove',
    });

    // TransformedMessage has compatible structure with ModelMessage (role + content)
    const messageLimit = 100;
    const transformedMessages = createMessageTransformer()(prunedMessages).slice(-messageLimit) as ModelMessage[];
    transformedMessages.push({
      role: 'system',
      content: `<system-reminder>
If you were unable to provide the results the user requested: do NOT tell the user what to do to fix it. Instead re-plan the task and try again.

If further context is needed to give a precise answer: eagerly fetch/transform supplementary source data using ${QueryKnowledgeGraphTool.mention}/${TransformDatasetTool.mention} before answering the user with precise, up-to-date information.

Use the ${AskUserQuestionTool.mention} tool to ask relevant follow-up questions to the user.
</system-reminder>
`,

      // - Use the ${ShowDashboardTool.mention} tool to display datasets and results from other tools; always avoid writing numbers as plain text. Single values can be displayed in a dashboard using KPI components. Lists and tabular data can be displayed using table components, and trends over time can be shown using line/bar/area charts. Always prefer visual presentation of data over text when possible.
      // - Be proactive in fetching and presenting relevant data to the user - don't wait for them to ask for supplementary data or KPIs. Proactively plan and run tool calls to include supplementary and useful contextual data in your response.
      // Refetch data using ${QueryKnowledgeGraphTool.mention} or ${FetchTimeseriesDatapointsTool.mention} and reprocess data using ${TransformDatasetTool.mention} without asking first.
      //
      // Reprocessing data is especially important as prior datasets may have filters or grouping applied; default to reprocessing source data from ${QueryKnowledgeGraphTool.mention} or ${FetchTimeseriesDatapointsTool.mention} with updated parameters to ensure you have the full context needed for accurate answers and insights.
      //
      // Be proactive in fetching and presenting relevant data to the user - don't wait for them to ask for it if you determine it would be useful for answering their question or providing insights.
      //
      // If further context is needed to give a precise answer: immediately fetch/process additional data using ${QueryKnowledgeGraphTool.mention}/${FetchTimeseriesDatapointsTool.mention}/${TransformDatasetTool.mention} before answering the user with precise, up-to-date information.
      //
      // Use the ${ShowDashboardTool.mention} tool to display results from other tools; avoid writing numbers as plain text. Single values can be displayed in a dashboard using KPI components. Lists and tabular data can be displayed using table components, and trends over time can be shown using line/bar/area charts. Always prefer visual presentation of data over text when possible.
      //
      // Ask relevant follow-up questions using the ${AskUserQuestionTool.mention} tool for higher-level follow-ups that require user input or decision-making.`,
    });

    console.debug('Processed messages for agent:', {
      original: messages,
      pruned: prunedMessages,
      transformed: transformedMessages,
    });

    return transformedMessages;
  }

  /**
   * Processes and streams messages to the LLM, returning a readable stream of UI message chunks.
   * Retrieves model context from runtime, processes messages with transformations, and configures streaming.
   */
  override async sendMessages(
    options: {
      chatId: string;
      messages: AtlasMessage[];
      abortSignal: AbortSignal | undefined;
    } & {
      trigger: 'submit-message' | 'regenerate-message';
      messageId: string | undefined;
    } & ChatRequestOptions,
  ): Promise<ReadableStream<UIMessageChunk>> {
    const modelContext = this.runtimeRef?.thread.getModelContext();
    const enabledTools = getEnabledTools(modelContext?.tools ?? {});
    const tools = frontendTools(enabledTools);
    try {
      const processedMessages = await this.processMessages(
        tools,
        options.messages.filter((message) => message.parts.length > 0),
      );

      // Debug: Track tool availability per request to diagnose duplicate/missing tool issues
      // See class documentation for investigation context
      const toolNames = Object.keys(enabledTools);
      console.debug('[AtlasChatTransport] sendMessages', {
        timestamp: dayjs().toISOString(),
        trigger: options.trigger,
        toolCount: toolNames.length,
        toolNames,
        tools,
        messageCount: options.messages.length,
        hasModelContext: !!modelContext,
        messages: { input: options.messages, processed: processedMessages },
      });

      const result = streamText({
        model: this.model,
        // biome-ignore lint/suspicious/noExplicitAny: frontendTools returns Schema<unknown>, AI SDK expects FlexibleSchema
        tools: tools as any as ToolSet,
        system: modelContext?.system,
        messages: processedMessages,
        abortSignal: options.abortSignal,
        ...modelContext?.callSettings,
      });

      return result.toUIMessageStream({
        onFinish: (...args: unknown[]) => {
          console.info('AtlasChatTransport stream finished', args);
        },
        originalMessages: options.messages,
        generateMessageId: createIdGenerator({ prefix: 'atlas' }),
        onError: (error: unknown) => {
          console.error('Error in AtlasChatTransport:', error);
          if (error == null) {
            return 'Unknown error';
          }
          if (typeof error === 'string') {
            return error;
          }
          if (error instanceof Error) {
            return error.message;
          }
          return JSON.stringify(error);
        },
      });
    } catch (error) {
      console.error('Error processing messages in AtlasChatTransport:', error);
      throw error;
    }
  }
}
